import { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, IconButton, List, ListItem,
  Tooltip, Typography,
} from '@mui/material';
import { Download, UploadFile } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  fetchReportHistory, downloadReportArtifact, saveIssuedReport,
} from '../../services/reportService';
import type { ReportArtifactSummary } from '../../types/reportArtifact';

interface ReportHistoryPanelProps {
  workOrderDetailKey: string;
  /** Bump to re-fetch (e.g. after saving an issued copy). */
  refreshToken: number;
  /** 'sidebar' (default) = fixed-width right rail inside the preview dialog;
   *  'dialog' = fluid width for standalone ReportHistoryDialog hosting. */
  variant?: 'sidebar' | 'dialog';
}

/** Matches the backend raw-body route limit (reportRoutes.ts). */
const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

/**
 * Report history sidebar of the Preview & Edit dialog: every archived render
 * ('Generated') and engineer-finalized copy ('Issued') for one test, newest
 * first, each downloadable (server verifies the stored PDF's sha256 first).
 */
export const ReportHistoryPanel = ({ workOrderDetailKey, refreshToken, variant = 'sidebar' }: ReportHistoryPanelProps) => {
  const theme = useTheme();
  const [items, setItems] = useState<ReportArtifactSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Internal bump so an in-panel upload refreshes without parent involvement.
  const [localRefresh, setLocalRefresh] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    fetchReportHistory(workOrderDetailKey)
      .then((rows) => { if (!cancelled) setItems(rows); })
      .catch(() => { if (!cancelled) setError('Failed to load report history.'); });
    return () => { cancelled = true; };
  }, [workOrderDetailKey, refreshToken, localRefresh]);

  /** Workflow A (external editor): the engineer downloads the report, adjusts
   *  it in Acrobat / Inkscape / LibreOffice Draw, and files the result here as
   *  the issued copy — same endpoint the in-dialog save uses, so it gets the
   *  same integrity hash, audit attribution, and history entry. */
  const handleUploadFile = async (file: File) => {
    setUploadError(null);
    if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
      setUploadError('Only PDF files can be uploaded.');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError('File exceeds the 100 MB upload limit.');
      return;
    }
    setUploading(true);
    try {
      await saveIssuedReport(workOrderDetailKey, file, file.name, 'Uploaded (edited externally)');
      setLocalRefresh((n) => n + 1);
    } catch {
      setUploadError('Upload failed — the file was not saved.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box
      sx={{
        width: variant === 'sidebar' ? 320 : '100%',
        flexShrink: 0,
        borderLeft: variant === 'sidebar' ? `1px solid ${theme.palette.divider}` : 'none',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
        ...(variant === 'dialog' && { maxHeight: '60vh' }),
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Report History
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Archived renders and issued copies
        </Typography>
        <Tooltip title="File a PDF you edited outside the app (e.g. in Adobe Acrobat) as this test's issued copy">
          <span style={{ display: 'block', marginTop: 8 }}>
            <Button
              size="small"
              variant="outlined"
              fullWidth
              startIcon={uploading ? <CircularProgress size={14} /> : <UploadFile />}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              sx={{ textTransform: 'none' }}
            >
              {uploading ? 'Uploading…' : 'Upload issued copy'}
            </Button>
          </span>
        </Tooltip>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Reset so picking the same file again re-triggers onChange.
            e.target.value = '';
            if (file) void handleUploadFile(file);
          }}
        />
        {uploadError && (
          <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5 }}>
            {uploadError}
          </Typography>
        )}
      </Box>

      {!items && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={22} />
        </Box>
      )}
      {error && (
        <Typography variant="body2" color="error" sx={{ p: 2 }}>{error}</Typography>
      )}
      {items && items.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No archived reports yet. Generating or issuing a report records it here.
        </Typography>
      )}

      {items && items.length > 0 && (
        <List dense disablePadding sx={{ overflowY: 'auto', flex: 1 }}>
          {items.map((item) => {
            const issued = item.kind === 'issued';
            return (
              <ListItem
                key={item.reportArtifactKey}
                divider
                secondaryAction={(
                  <Tooltip title="Download this copy">
                    <IconButton
                      edge="end"
                      size="small"
                      aria-label="Download archived report"
                      onClick={() => {
                        downloadReportArtifact(workOrderDetailKey, item.reportArtifactKey)
                          .catch(() => { /* surfaced by global interceptor */ });
                      }}
                    >
                      <Download sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
                sx={{ alignItems: 'flex-start', py: 1 }}
              >
                <Box sx={{ minWidth: 0, pr: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                    <Chip
                      label={issued ? 'Issued' : 'Generated'}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.66rem',
                        fontWeight: 700,
                        color: issued ? theme.customColors.maroon : theme.palette.primary.dark,
                        backgroundColor: alpha(
                          issued ? theme.customColors.maroon : theme.palette.primary.main,
                          0.12,
                        ),
                      }}
                    />
                    {item.appVersion && (
                      <Typography variant="caption" color="text.secondary">
                        {item.appVersion}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, wordBreak: 'break-all', lineHeight: 1.3 }}
                  >
                    {item.fileName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {formatWhen(item.createdDate)} · {item.createdBy} · {formatBytes(item.byteSize)}
                  </Typography>
                  {item.note && (
                    <Typography variant="caption" component="div" sx={{ fontStyle: 'italic' }}>
                      {item.note}
                    </Typography>
                  )}
                </Box>
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
};
